import { useEffect, useState } from 'react';
import axios from 'axios';

interface HistoryEntry {
  totalSpend: number;
  currency: string;
  periodStart: string;
  periodEnd: string;
  serviceBreakdown: Record<string, number>;
}

const FONT = '"Plus Jakarta Sans", system-ui, sans-serif';

interface Props {
  accounts: { id: string; account_id: string }[];
  isDark: boolean;
}

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatMonth(dateStr: string) {
  const d = new Date(dateStr);
  return `${MONTH_LABELS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export default function TrendsView({ accounts, isDark }: Props) {
  const [selectedId, setSelectedId] = useState(accounts[0]?.id || '');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; entry: HistoryEntry } | null>(null);

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/costs/aws-accounts/${selectedId}/history`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => setHistory(r.data))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const textMuted = isDark ? 'rgba(255,255,255,0.38)' : 'rgba(15,22,32,0.42)';
  const cardBg = isDark ? 'rgba(17,24,39,0.8)' : '#ffffff';
  const cardShadow = isDark ? 'none' : '0 1px 3px rgba(15,22,32,0.06), 0 4px 16px rgba(15,22,32,0.04)';
  const cardBorder = isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,22,32,0.06)';
  const gridColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,22,32,0.05)';

  // chart dimensions
  const W = 700, H = 260, PAD = { top: 20, right: 20, bottom: 48, left: 70 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const maxSpend = Math.max(...history.map(h => h.totalSpend), 1);
  const barW = history.length > 0 ? Math.min(60, (chartW / history.length) * 0.6) : 40;
  const barGap = history.length > 0 ? chartW / history.length : 80;

  // top services across all months (for the breakdown table)
  const aggregatedServices: Record<string, number> = {};
  history.forEach(h => {
    Object.entries(h.serviceBreakdown || {}).forEach(([k, v]) => {
      aggregatedServices[k] = (aggregatedServices[k] || 0) + v;
    });
  });
  const topServices = Object.entries(aggregatedServices)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6);

  // MoM change
  const mom = history.length >= 2
    ? ((history[history.length - 1].totalSpend - history[history.length - 2].totalSpend) / history[history.length - 2].totalSpend) * 100
    : null;

  const yGridLines = 5;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', fontFamily: FONT }}>
      {/* Account tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        {accounts.map(a => (
          <button
            key={a.id}
            onClick={() => setSelectedId(a.id)}
            style={{
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'ui-monospace, monospace',
              cursor: 'pointer',
              transition: 'all 0.15s',
              ...(selectedId === a.id
                ? { background: '#0F3D2E', color: '#fff', border: '1px solid #0F3D2E' }
                : { background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,22,32,0.05)', color: textMuted, border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,22,32,0.08)'}` })
            }}
          >
            {a.account_id || a.id}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0' }}>
          <div style={{ width: '24px', height: '24px', borderRadius: '50%', border: '2px solid #0F3D2E', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />
        </div>
      ) : history.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '80px 0', fontSize: '13px', color: textMuted }}>
          No historical data available
        </div>
      ) : (
        <>
          {/* Summary row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px' }}>
            {[
              {
                label: 'Latest Month',
                value: `$${history[history.length - 1].totalSpend.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
                sub: formatMonth(history[history.length - 1].periodStart),
              },
              {
                label: 'MoM Change',
                value: mom !== null ? `${mom >= 0 ? '+' : ''}${mom.toFixed(1)}%` : '—',
                sub: 'vs previous month',
                valueColor: mom !== null ? (mom > 5 ? '#DC2626' : mom < -5 ? '#16A34A' : undefined) : undefined,
              },
              {
                label: '6-Month Total',
                value: `$${history.reduce((s, h) => s + h.totalSpend, 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
                sub: `across ${history.length} months`,
              },
            ].map(card => (
              <div key={card.label} style={{ background: cardBg, borderRadius: '14px', padding: '18px 20px', boxShadow: cardShadow, border: cardBorder }}>
                <p style={{ fontSize: '11px', fontWeight: 500, color: textMuted, marginBottom: '10px' }}>{card.label}</p>
                <p style={{ fontSize: '22px', fontWeight: 700, color: card.valueColor || (isDark ? '#fff' : '#07281D'), marginBottom: '4px' }}>{card.value}</p>
                <p style={{ fontSize: '12px', color: textMuted }}>{card.sub}</p>
              </div>
            ))}
          </div>

          {/* Bar chart */}
          <div style={{ background: cardBg, borderRadius: '16px', padding: '22px 24px', boxShadow: cardShadow, border: cardBorder }}>
            <p style={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#fff' : '#07281D', marginBottom: '20px' }}>Monthly Spend</p>
            <div style={{ position: 'relative' }}>
              <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ overflow: 'visible' }}>
                {/* Y grid lines */}
                {Array.from({ length: yGridLines + 1 }, (_, i) => {
                  const y = PAD.top + (chartH / yGridLines) * i;
                  const val = maxSpend * (1 - i / yGridLines);
                  return (
                    <g key={i}>
                      <line x1={PAD.left} x2={PAD.left + chartW} y1={y} y2={y}
                        stroke={gridColor} strokeWidth={1} />
                      <text x={PAD.left - 8} y={y + 4} textAnchor="end"
                        fontSize={10} fill={textMuted}>
                        ${val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val.toFixed(0)}
                      </text>
                    </g>
                  );
                })}

                {/* Bars */}
                {history.map((entry, i) => {
                  const barH = (entry.totalSpend / maxSpend) * chartH;
                  const x = PAD.left + barGap * i + (barGap - barW) / 2;
                  const y = PAD.top + chartH - barH;
                  const isLatest = i === history.length - 1;
                  return (
                    <g key={i}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={e => {
                        const rect = (e.currentTarget as SVGGElement).getBoundingClientRect();
                        setTooltip({ x: rect.left + rect.width / 2, y: rect.top, entry });
                      }}
                      onMouseLeave={() => setTooltip(null)}
                    >
                      <rect
                        x={x} y={y} width={barW} height={barH}
                        rx={5}
                        fill={isLatest ? '#0F3D2E' : isDark ? 'rgba(15,61,46,0.35)' : 'rgba(15,61,46,0.55)'}
                        opacity={1}
                      />
                      {/* Month label */}
                      <text
                        x={x + barW / 2}
                        y={PAD.top + chartH + 18}
                        textAnchor="middle"
                        fontSize={10}
                        fill={textMuted}
                      >
                        {formatMonth(entry.periodStart)}
                      </text>
                      {/* Value on top */}
                      <text
                        x={x + barW / 2}
                        y={y - 6}
                        textAnchor="middle"
                        fontSize={10}
                        fontWeight="600"
                        fill={isLatest ? '#0F3D2E' : textMuted}
                      >
                        ${entry.totalSpend >= 1000 ? `${(entry.totalSpend / 1000).toFixed(1)}k` : entry.totalSpend.toFixed(0)}
                      </text>
                    </g>
                  );
                })}
              </svg>

              {/* Tooltip */}
              {tooltip && (
                <div style={{
                  position: 'fixed',
                  left: tooltip.x,
                  top: tooltip.y - 12,
                  transform: 'translate(-50%, -100%)',
                  background: isDark ? '#1f2937' : '#ffffff',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,22,32,0.1)'}`,
                  borderRadius: '10px',
                  padding: '12px 16px',
                  pointerEvents: 'none',
                  zIndex: 999,
                  minWidth: '190px',
                  boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.4)' : '0 8px 32px rgba(15,22,32,0.12)',
                  fontFamily: FONT,
                }}>
                  <p style={{ fontSize: '11px', fontWeight: 500, color: textMuted, marginBottom: '4px' }}>{formatMonth(tooltip.entry.periodStart)}</p>
                  <p style={{ fontSize: '20px', fontWeight: 700, color: isDark ? '#fff' : '#07281D', marginBottom: '10px' }}>
                    ${tooltip.entry.totalSpend.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                  <div style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(15,22,32,0.07)'}`, paddingTop: '8px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {Object.entries(tooltip.entry.serviceBreakdown || {})
                      .sort(([, a], [, b]) => b - a)
                      .slice(0, 4)
                      .map(([svc, cost]) => (
                        <div key={svc} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                          <span style={{ fontSize: '11px', color: textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{svc.replace('Amazon ', '').replace('AWS ', '')}</span>
                          <span style={{ fontSize: '11px', fontFamily: 'ui-monospace, monospace', color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,22,32,0.6)', flexShrink: 0 }}>${Number(cost).toFixed(0)}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Top services table */}
          <div style={{ background: cardBg, borderRadius: '16px', padding: '22px 24px', boxShadow: cardShadow, border: cardBorder }}>
            <p style={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#fff' : '#07281D', marginBottom: '18px' }}>Top Services — 6-month total</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {topServices.map(([svc, total], i) => {
                const pct = (total / (topServices[0][1] || 1)) * 100;
                const colors = ['#0F3D2E', '#0F3D2E', '#4E7D5E', '#0284C7', '#0D9488', '#DC2626'];
                const color = colors[i % colors.length];
                return (
                  <div key={svc}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 500, color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,22,32,0.7)' }}>
                        {svc.replace('Amazon ', '').replace('AWS ', '')}
                      </span>
                      <span style={{ fontSize: '12px', fontFamily: 'ui-monospace, monospace', fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,22,32,0.55)' }}>
                        ${total.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div style={{ height: '5px', borderRadius: '3px', background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(15,22,32,0.05)' }}>
                      <div style={{ height: '100%', borderRadius: '3px', width: `${pct}%`, background: color, opacity: 0.75 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
